import React, { useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Button,
  Box,
  Text,
  Checkbox,
  VStack,
  HStack,
  Tag,
  TagLabel,
  TagCloseButton,
  useColorModeValue,
  Grid,
  GridItem,
  Link,
  Flex,
} from '@chakra-ui/react';
import type { MyLeague } from '../store/myLeagueStore';

interface TeamMultiSelectProps {
  league: MyLeague | null;
  selectedTeamIds: (string | number)[];
  onChange: (teamIds: (string | number)[]) => void;
  placeholder?: string;
}

const TeamMultiSelect: React.FC<TeamMultiSelectProps> = ({
  league,
  selectedTeamIds,
  onChange,
  placeholder = 'Select Teams',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const popoverBg = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');

  if (!league || !league.conferences) {
    return null;
  }

  const toggleTeam = (teamId: string | number) => {
    const isSelected = selectedTeamIds.includes(teamId);
    if (isSelected) {
      onChange(selectedTeamIds.filter((id) => id !== teamId));
    } else {
      onChange([...selectedTeamIds, teamId]);
    }
  };

  return (
    <Box>
      <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement="bottom-start">
        <PopoverTrigger>
          <Button
            onClick={() => setIsOpen(!isOpen)}
            variant="outline"
            colorScheme="blue"
            size="sm"
            width="200px"
          >
            {placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent bg={popoverBg} borderColor={borderColor} maxW="6xl" maxH="600px" overflowY="auto">
          <PopoverBody p={4}>
            <VStack align="stretch" spacing={4}>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="bold" fontSize="lg">
                  Select Teams
                </Text>
                {selectedTeamIds.length > 0 && (
                  <Link
                    color="blue.500"
                    fontSize="sm"
                    onClick={() => onChange([])}
                    _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Clear
                  </Link>
                )}
              </Flex>
              <Grid templateColumns={{ base: '1fr', md: `repeat(${league.conferences.length}, 1fr)` }} gap={6}>
                {league.conferences.map((conf) => (
                  <GridItem key={conf.id}>
                    <Box mb={4}>
                      <Text fontWeight="semibold" fontSize="md" mb={3} color="blue.600" whiteSpace="nowrap">
                        {conf.name}
                      </Text>
                      <VStack align="stretch" spacing={3}>
                        {conf.divisions.map((div) => (
                          <Box key={div.id}>
                            <Text fontWeight="medium" fontSize="sm" color="gray.600" mb={2}>
                              {div.name}
                            </Text>
                            <Box bg={cardBg} borderRadius="md" borderWidth="1px" borderColor={borderColor} p={2}>
                              <VStack align="stretch" spacing={1}>
                                {div.teams.map((team) => {
                                  const isSelected =
                                    selectedTeamIds.includes(team.id) ||
                                    selectedTeamIds.some((id) => String(id) === String(team.id));
                                  return (
                                    <Checkbox
                                      key={team.id}
                                      isChecked={isSelected}
                                      onChange={() => toggleTeam(team.id)}
                                      size="sm"
                                    >
                                      {team.name}
                                    </Checkbox>
                                  );
                                })}
                              </VStack>
                            </Box>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </GridItem>
                ))}
              </Grid>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export const renderTeamChips = (
  selectedTeamIds: (string | number)[],
  league: MyLeague | null,
  onRemove: (teamId: string | number) => void
) => {
  if (!league || selectedTeamIds.length === 0) {
    return null;
  }

  const getTeamName = (teamId: string | number): string => {
    for (const conf of league.conferences) {
      for (const div of conf.divisions) {
        for (const team of div.teams) {
          if (team.id === teamId || String(team.id) === String(teamId)) {
            return team.name;
          }
        }
      }
    }
    return `Team ${teamId}`;
  };

  return (
    <HStack spacing={2} flexWrap="wrap">
      {selectedTeamIds.map((teamId) => (
        <Tag key={teamId} size="md" colorScheme="blue" borderRadius="full">
          <TagLabel>{getTeamName(teamId)}</TagLabel>
          <TagCloseButton onClick={() => onRemove(teamId)} />
        </Tag>
      ))}
    </HStack>
  );
};

export default TeamMultiSelect;
